/**
 * Post Detail Modal - Full post view with comments and interactions
 */

import { useState, useEffect } from "react";
import { Heart, MessageCircle, X, Send, Car, Store, Share2, Plus, Loader2, MoreHorizontal } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "wouter";
import type { Post } from "@shared/schema";
import { getAuthHeader } from "@/lib/authHelpers";
import { generateShopSlug, generateRouteSlug } from "@/lib/slugify";
import { useI18n } from "@/lib/i18n";
import { TranslatableText } from "@/components/TranslatableText";
import { getShopProfileUrl } from "@/lib/shopUrl";
import { ShareModal } from "@/components/ShareModal";
import { useToast } from "@/hooks/use-toast";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { WishlistItemFormDialog } from "./WishlistItemFormDialog";
import { getToken } from "@/lib/authContext";

interface PostDetailModalProps {
  post: Post | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onLikeChange?: () => void;
}

interface Comment {
  id: string;
  userId: string;
  content: string;
  translations?: Record<string, string>;
  createdAt: string;
  authorUsername: string;
  authorAvatarUrl?: string | null;
}

export function PostDetailModal({ post, open, onOpenChange, onLikeChange }: PostDetailModalProps) {
  const { user, isLoggedIn } = useAuth();
  const { t, locale } = useI18n();
  const [, setLocation] = useLocation();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(post?.likesCount || 0);
  const [commentsCount, setCommentsCount] = useState(post?.commentsCount || 0);
  const [commentText, setCommentText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [wishlistModalOpen, setWishlistModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Load comments and like status when modal opens
  useEffect(() => {
    if (open && post) {
      loadComments();
      checkLiked();
      setLikeCount(post.likesCount || 0);
      setCommentsCount(post.commentsCount || 0);
    }
  }, [open, post]);

  const addWishlistMutation = useMutation({
    mutationFn: async (payload: any) => {
      const token = await getToken();
      if (!token) throw new Error(t("postComposer.loginRequired"));

      const res = await fetch("/api/wishlist/items", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error("Failed to add item");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["my-wishlist"] });
      setWishlistModalOpen(false);
      toast({ title: t("itemWishlist.addedTitle"), description: t("itemWishlist.addedDesc") });
    },
    onError: (err: Error) => {
      toast({ variant: "destructive", title: t("common.error"), description: err.message });
    },
  });

  const uploadImage = async (base64: string): Promise<{ url: string; thumbnail: string }> => {
    const token = await getToken();
    if (!token) throw new Error("Login required");

    const res = await fetch("/api/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ images: [base64], bucket: "wishlist-images" }),
    });
    if (!res.ok) throw new Error("Upload failed");
    const data = await res.json();
    return { url: data.urls[0], thumbnail: data.thumbnails?.[0] || data.urls[0] };
  };

  async function loadComments() {
    if (!post) return;

    try {
      setLoading(true);
      const response = await fetch(`/api/posts/${post.id}/comments`);
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error("Error loading comments:", error);
    } finally {
      setLoading(false);
    }
  }

  async function checkLiked() {
    if (!post || !isLoggedIn || !user) return;

    try {
      const headers = await getAuthHeader();
      const response = await fetch(`/api/posts/${post.id}`, {
        headers: headers || {},
      });
      if (response.ok) {
        const data = await response.json();
        setLiked(data.userLiked || false);
      }
    } catch (error) {
      console.error("Error checking like status:", error);
    }
  }

  async function handleLike() {
    if (!post || !isLoggedIn) {
      // Show login modal or redirect
      return;
    }

    try {
      const headers = await getAuthHeader();
      const response = await fetch(`/api/posts/${post.id}/like`, {
        method: "POST",
        headers: headers || {},
      });

      if (response.ok) {
        const data = await response.json();
        setLiked(data.liked);
        setLikeCount(data.likeCount);
        if (onLikeChange) {
          onLikeChange();
        }
      }
    } catch (error) {
      console.error("Error toggling like:", error);
    }
  }

  async function handleComment() {
    if (!post || !isLoggedIn || !commentText.trim()) return;

    try {
      setSubmitting(true);
      const authHeaders = await getAuthHeader();
      const response = await fetch(`/api/posts/${post.id}/comments`, {
        method: "POST",
        headers: {
          ...(authHeaders || {}),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ content: commentText.trim() }),
      });

      if (response.ok) {
        const newComment = await response.json();
        setComments([newComment, ...comments]);
        setCommentText("");
        setCommentsCount((prev: number) => prev + 1);

        // Notify parent to refresh counts
        if (onLikeChange) onLikeChange();
      }
    } catch (error) {
      console.error("Error adding comment:", error);
    } finally {
      setSubmitting(false);
    }
  }

  function handleUserClick(userId: string) {
    setLocation(`/users/${userId}`);
    onOpenChange(false);
  }

  async function handleShopClick(shopId: string) {
    try {
      const response = await fetch(`/api/shops/${shopId}`);
      if (response.ok) {
        const shop = await response.json();
        setLocation(getShopProfileUrl(shop, locale));
      } else {
        setLocation(`/shops/${shopId}`);
      }
    } catch (error) {
      setLocation(`/shops/${shopId}`);
    }
    onOpenChange(false);
  }

  async function handleRouteClick(routeId: string) {
    try {
      const response = await fetch(`/api/routes/${routeId}`);
      if (response.ok) {
        const route = await response.json();
        if (route.slug) {
          setLocation(`/kringlooproutes/${route.slug}`);
        } else if (route.name) {
          setLocation(`/kringlooproutes/${generateRouteSlug(route.name)}`);
        } else {
          setLocation(`/routes/${routeId}`);
        }
      } else {
        setLocation(`/routes/${routeId}`);
      }
    } catch (error) {
      setLocation(`/routes/${routeId}`);
    }
    onOpenChange(false);
  }

  if (!post) return null;

  const images = (post.images && Array.isArray(post.images) ? post.images : [post.imageUrl]).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="sr-only">{t("postDetail.title")}</DialogTitle>
          <DialogDescription className="sr-only">
            {t("postDetail.fullViewDesc")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Images */}
          <div className="space-y-2">
            {images.length > 0 && (
              <div className="aspect-square relative overflow-hidden rounded-lg">
                <img
                  src={images[0]}
                  alt={post.caption}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
            {images.length > 1 && (
              <div className="grid grid-cols-2 gap-2">
                {images.slice(1, 4).map((img, idx) => (
                  <div key={idx} className="aspect-square relative overflow-hidden rounded-lg">
                    <img src={img} alt={`${post.caption} ${idx + 2}`} className="w-full h-full object-cover" />
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Content */}
          <div className="flex flex-col space-y-4">
            {/* Author */}
            <div className="flex items-center gap-3">
              <Avatar className="h-10 w-10">
                {post.authorAvatarUrl ? (
                  <AvatarImage src={post.authorAvatarUrl} alt={post.authorUsername} />
                ) : null}
                <AvatarFallback>
                  {post.authorUsername?.[0].toUpperCase() || 'U'}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <button
                  onClick={() => handleUserClick(post.authorId)}
                  className="font-semibold hover:underline"
                >
                  {post.authorUsername}
                </button>
                <div className="flex items-center gap-2 mt-1">
                  <Badge variant="secondary" className="text-xs">
                    {post.category}
                  </Badge>
                  {(post as any).pricePaid !== null && (post as any).pricePaid !== undefined && (
                    <Badge variant="outline" className="text-xs border-primary/20 bg-primary/5 text-primary">
                      {new Intl.NumberFormat(locale === "nl" ? "nl-NL" : "en-US", {
                        style: "currency",
                        currency: (post as any).currency || "EUR",
                      }).format((post as any).pricePaid / 100)}
                    </Badge>
                  )}
                </div>
              </div>
              <button
                className="text-muted-foreground hover:text-foreground p-1"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isLoggedIn) return;
                  setWishlistModalOpen(true);
                }}
              >
                <Heart className="h-5 w-5" />
              </button>
            </div>

            {/* Caption */}
            <div className="flex-1">
              <TranslatableText
                table="gems"
                rowId={post.id}
                text={post.caption}
                translations={(post as any).translations}
                className="text-sm whitespace-pre-wrap"
              />
            </div>

            {/* Shop/Route links */}
            {post.shopId && (
              <div>
                <button
                  onClick={() => handleShopClick(post.shopId!)}
                  className="text-sm text-muted-foreground hover:underline flex items-center gap-1"
                >
                  <Store className="h-4 w-4" />
                  {t("postDetail.viewShop")}
                </button>
              </div>
            )}
            {post.routeId && (
              <div>
                <button
                  onClick={() => handleRouteClick(post.routeId!)}
                  className="text-sm text-muted-foreground hover:underline flex items-center gap-1"
                >
                  <Car className="h-4 w-4" />
                  {t("postDetail.viewRoute")}
                </button>
              </div>
            )}

            {/* Actions */}
            <div className="flex items-center gap-4 border-t border-b py-4">
              <button
                onClick={handleLike}
                className={`flex items-center gap-2 ${liked ? "text-primary" : "text-muted-foreground hover:text-primary"}`}
              >
                <Heart className={`h-5 w-5 ${liked ? "fill-current" : ""}`} />
                <span>{likeCount}</span>
              </button>
              <div className={`flex items-center gap-2 mr-auto ${commentsCount > 0 ? "text-primary" : "text-muted-foreground"}`}>
                <MessageCircle className={`h-5 w-5 ${commentsCount > 0 ? "fill-current opacity-20" : ""}`} />
                <span>{commentsCount}</span>
              </div>
              <button
                onClick={() => setShareModalOpen(true)}
                className="flex items-center gap-2 text-muted-foreground hover:text-primary"
              >
                <Share2 className="h-5 w-5" />
                <span className="text-sm font-medium">{t("common.share")}</span>
              </button>
            </div>

            {/* Comments */}
            <div className="flex-1 space-y-4 max-h-[300px] overflow-y-auto">
              <h3 className="font-semibold text-sm">{t("postDetail.comments")}</h3>
              {loading ? (
                <p className="text-sm text-muted-foreground">{t("routeDetail.loading")}</p>
              ) : comments.length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("postDetail.noComments")}</p>
              ) : (
                <div className="space-y-3">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex gap-3">
                      <Avatar className="h-8 w-8">
                        {comment.authorAvatarUrl ? (
                          <AvatarImage src={comment.authorAvatarUrl} alt={comment.authorUsername} />
                        ) : null}
                        <AvatarFallback className="text-xs">
                          {comment.authorUsername?.[0].toUpperCase() || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <button
                          onClick={() => handleUserClick(comment.userId)}
                          className="font-semibold text-sm hover:underline mr-2"
                        >
                          {comment.authorUsername}
                        </button>
                        <TranslatableText
                          table="comments"
                          rowId={comment.id}
                          text={comment.content}
                          translations={comment.translations}
                          className="text-sm"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {new Date(comment.createdAt).toLocaleDateString(locale === "nl" ? "nl-NL" : locale === "en-gb" ? "en-GB" : `${locale}-${locale.toUpperCase()}`)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Comment input */}
            {isLoggedIn ? (
              <div className="flex gap-2">
                <Textarea
                  placeholder={t("postDetail.addComment")}
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  className="min-h-[60px] resize-none"
                  rows={2}
                />
                <Button
                  onClick={handleComment}
                  disabled={!commentText.trim() || submitting}
                  size="icon"
                  className="self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center">
                {t("postDetail.loginToComment")}
              </p>
            )}
          </div>
        </div>
      </DialogContent>
      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        title={t("gems.shareTitle")}
        text={t("gems.shareText", { caption: post.caption })}
        url={`${window.location.origin}/community?post=${post.id}`}
        imageUrl={(post as any).share_image_url || images[0] || post.imageUrl || undefined}
        entityType="post"
        entityId={post.id}
      />

      <WishlistItemFormDialog
        open={wishlistModalOpen}
        onClose={() => setWishlistModalOpen(false)}
        initialData={{
          item_name: post.category || "",
          image_url: images[0] || post.imageUrl || undefined,
          image_thumbnail_url: images[0] || post.imageUrl || undefined,
          notes: post.caption || undefined,
        }}
        onSubmit={(data) => addWishlistMutation.mutate(data)}
        isLoading={addWishlistMutation.isPending}
        uploadImage={uploadImage}
      />
    </Dialog>
  );
}
